import { constructValue } from '@adornis/baseql/entities/construct.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import '@adornis/buchungsheld-theme/components/tr-dropdown-search-selection.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import '@adornis/entity-picker/client/x-entity-picker.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/forms/x-input.js';
import '@adornis/forms/x-textarea.js';
import { guardDialog, promptDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { servePdfFromComponent } from '@adornis/print/db/print.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { format } from 'date-fns';
import { html, nothing, type TemplateResult } from 'lit';
import { map, of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { getLocale } from '../../../../localization/out/runtime.js';
import {
  confirmInterviewAttendance,
  subscribeJobApplicationsWithFilter,
  subscribeNumberOfFreeSlotsAvailable,
  updateJobApplicationStatus,
  updateJobApplicationStatuses,
} from '../../../api/job-application.js';
import { getJobPositionByID } from '../../../api/job-position.js';
import { JobApplication, JobApplicationStatus } from '../../../db/job-application.js';
import type { JobPosition } from '../../../db/job-position.js';
import { MatchingScore } from '../../../db/MatchingScore.js';
import { normalizeHttpUrl } from '../../../shared/url.js';
import { DefaultContainerLoader, MessagedContainerLoader } from '../../components/loaders.js';
import {
  createSelectionStore,
  isEmptySelection,
  isIDSelected,
  type SelectionStore,
  selectionCount,
} from '../../components/selection-state.js';
import { TimeSlotsDisplay } from '../../components/time-slots-display.js';
import { translateEnum } from '../../translate-enum.js';
import { companyJobRouter } from './company-routes.js';

type StatusColorClasses = { header: string; bar: string; border: string; cardBg: string };

// Per-tone palettes. Class names must be spelled out literally so Tailwind's
// compiler can emit them — a `${tone}` template-literal factory would render
// blank because Tailwind only scans literal class strings.
const SUCCESS_COLORS: StatusColorClasses = {
  header: 'bg-success/20 text-success-content',
  bar: 'bg-success',
  border: 'border-success/30',
  cardBg: 'bg-success/10',
};
const ERROR_COLORS: StatusColorClasses = {
  header: 'bg-error/20 text-error-content',
  bar: 'bg-error',
  border: 'border-error/30',
  cardBg: 'bg-error/10',
};
const WARNING_COLORS: StatusColorClasses = {
  header: 'bg-warning/20 text-warning-content',
  bar: 'bg-warning',
  border: 'border-warning/30',
  cardBg: 'bg-warning/10',
};
const INFO_COLORS: StatusColorClasses = {
  header: 'bg-info/20 text-info-content',
  bar: 'bg-info',
  border: 'border-info/30',
  cardBg: 'bg-info/10',
};

const STATUS_COLORS: Record<JobApplicationStatus, StatusColorClasses> = {
  [JobApplicationStatus.ACCEPTED]: SUCCESS_COLORS,
  [JobApplicationStatus.REJECTED]: ERROR_COLORS,
  [JobApplicationStatus.REJECTED_BY_USER]: ERROR_COLORS,
  [JobApplicationStatus.WAITING]: WARNING_COLORS,
  [JobApplicationStatus.BOOKED]: INFO_COLORS,
  [JobApplicationStatus.PENDING]: WARNING_COLORS,
};

// Explicit list — `Object.values(JobApplicationStatus)` would include baseql's
// `_class` metadata as a stray string, so any new status must be added here
// and to `STATUS_COLORS` together.
const STATUS_ORDER: JobApplicationStatus[] = [
  JobApplicationStatus.PENDING,
  JobApplicationStatus.ACCEPTED,
  JobApplicationStatus.BOOKED,
  JobApplicationStatus.WAITING,
  JobApplicationStatus.REJECTED,
  JobApplicationStatus.REJECTED_BY_USER,
];

const getOrderedStatuses = (): JobApplicationStatus[] => STATUS_ORDER;

// `iconClass` must be a literal string at every call site — `icon-[material-symbols--*]`
// is an arbitrary-value Tailwind class and only emitted if the compiler sees the full
// class name literally in source.
const renderRowActionButton = (cfg: {
  enabled: boolean;
  title: string;
  colorClass: string;
  iconClass: string;
  onClick: () => void;
}): TemplateResult => html`
  <button
    class="btn btn-ghost btn-xs ${
      cfg.enabled ? cfg.colorClass : 'text-base-content/30 btn-disabled opacity-50 cursor-default'
    }"
    title=${cfg.title}
    ?disabled=${!cfg.enabled}
    @click=${(e: Event) => {
      e.stopPropagation();
      if (!cfg.enabled) return;
      cfg.onClick();
    }}
  >
    <span class="${cfg.iconClass} w-5 h-5"></span>
  </button>
`;

const renderBulkActionButton = (cfg: {
  enabled: boolean;
  title: string;
  colorClass: string;
  iconClass: string;
  label: string;
  onClick: () => void;
}): TemplateResult => html`
  <button
    class="btn btn-sm ${cfg.colorClass} ${cfg.enabled ? '' : 'btn-disabled opacity-60'}"
    ?disabled=${!cfg.enabled}
    title=${cfg.title}
    @click=${cfg.onClick}
  >
    <span class="${cfg.iconClass}"></span>
    ${cfg.label}
  </button>
`;

const StatusTable = stateful(
  (props: {
    companyID: string;
    jobPositionID: string;
    status: JobApplicationStatus;
    freeSlots: number;
    /** IDs of every application currently in this status. */
    ids: string[];
    /** Shared selection store — same instance across every StatusTable on the page. */
    store: SelectionStore;
  }) => {
    // propsRef bridges latest values into the useMemo-frozen DataTable
    // options. Without it the header checkbox and per-row buttons would
    // read the ids/freeSlots captured at first render and never update.
    const [propsRef] = useState({ ids: props.ids, freeSlots: props.freeSlots });
    propsRef.ids = props.ids;
    propsRef.freeSlots = props.freeSlots;

    const store = props.store;
    const color = STATUS_COLORS[props.status];

    const canAccept = props.status !== JobApplicationStatus.ACCEPTED;
    const canPutOnWaitingList = props.status !== JobApplicationStatus.WAITING;
    const canReject = props.status !== JobApplicationStatus.REJECTED;

    const runRowAction = (id: string, target: JobApplicationStatus, errMsg: string) => {
      store.toggle(id, false);
      updateJobApplicationStatus(id, target)().catch(err => {
        console.error('Failed to update application', err);
        XSnackbar.show(errMsg);
      });
    };

    const options = useMemo(() => {
      const cols: ColumnDefinition<typeof JobApplication>[] = [
        {
          customID: '_select',
          // `columnName` is typed `string | null`, but lit-html renders a
          // directive in this slot — cast satisfies the lib type only.
          columnName: obs(
            store.observable.pipe(
              map(sel => {
                const ids = propsRef.ids;
                const checked = ids.length > 0 && ids.every(id => sel.includes(id));
                const indeterminate = !checked && ids.some(id => sel.includes(id));
                // The library wraps `columnName` in `<span class="flex-1">` —
                // without our own centering div the checkbox sticks to the
                // top of the (taller) header cell.
                return html`
                  <div class="flex items-center justify-center h-full">
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm"
                      .checked=${checked}
                      .indeterminate=${indeterminate}
                      @click=${(e: Event) => e.stopPropagation()}
                      @change=${(e: Event) =>
                        store.toggleSubset(propsRef.ids, (e.currentTarget as HTMLInputElement).checked)}
                    />
                  </div>
                `;
              }),
            ),
          ) as any,
          columnWidth: '3rem',
          isSortable: false,
          renderItem: (application: JobApplication) =>
            obs(
              store.observable.pipe(
                map(sel => {
                  const checked = isIDSelected(sel, application._id!);
                  return html`
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm"
                      .checked=${checked}
                      @click=${(e: Event) => e.stopPropagation()}
                      @change=${(e: Event) =>
                        store.toggle(application._id!, (e.currentTarget as HTMLInputElement).checked)}
                    />
                  `;
                }),
              ),
            ),
        },
        { columnName: 'Vorname', fieldName: 'user.firstName' },
        { columnName: 'Nachname', fieldName: 'user.lastName' },
        { columnName: 'Studiengang', fieldName: 'userData.studyField', isSortable: false },
        {
          columnName: 'Score',
          fieldName: 'matchingScore.total',
          renderItem: (application: JobApplication) => html`${application.matchingScore?.total ?? '-'}`,
          columnWidth: '7rem',
        },
      ];

      if (props.status === JobApplicationStatus.BOOKED) {
        cols.push({
          columnName: 'Vorstellungsgespräch',
          customID: 'interview',
          renderItem: (application: JobApplication) =>
            html`${
              application.interviewStart ? format(new Date(application.interviewStart), 'dd.MM.yyyy, HH:mm') : '-'
            }`,
          columnWidth: '12rem',
        });
      }

      cols.push({
        customID: '_options',
        columnName: '',
        columnWidth: 'max-content',
        isSortable: false,
        renderItem: (application: JobApplication) => {
          // Live-read freeSlots from propsRef — props captured by useMemo
          // goes stale when the slot count changes between renders.
          const canAcceptRow = canAccept && propsRef.freeSlots > 0;
          return html`
            <div class="flex items-center gap-1 whitespace-nowrap">
              ${renderRowActionButton({
                enabled: canAcceptRow,
                title: m.applicants_bulk_accept_button(),
                colorClass: 'text-success',
                iconClass: 'icon-[material-symbols--check-circle]',
                onClick: () => runRowAction(application._id!, JobApplicationStatus.ACCEPTED, m.accept_error()),
              })}
              ${renderRowActionButton({
                enabled: canPutOnWaitingList,
                title: m.applicants_bulk_waiting_button(),
                colorClass: 'text-warning',
                iconClass: 'icon-[material-symbols--schedule]',
                onClick: () => runRowAction(application._id!, JobApplicationStatus.WAITING, m.waiting_list_error()),
              })}
              ${renderRowActionButton({
                enabled: canReject,
                title: m.applicants_bulk_reject_button(),
                colorClass: 'text-error',
                iconClass: 'icon-[material-symbols--close]',
                onClick: () => runRowAction(application._id!, JobApplicationStatus.REJECTED, m.decline_error()),
              })}
              ${
                props.status === JobApplicationStatus.BOOKED
                  ? html`
                      <div class="join">
                        <button
                          class="btn btn-xs join-item ${
                            application.interviewAttended === true ? 'btn-success' : 'btn-outline btn-success'
                          }"
                          title=${
                            application.interviewAttended === true
                              ? m.interview_attendance_reset_tooltip()
                              : m.applicants_bulk_attendance_confirm_button()
                          }
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            confirmInterviewAttendance(
                              application._id!,
                              application.interviewAttended === true ? null : true,
                            )()
                              .then(() => XSnackbar.show(m.interview_attendance_saved()))
                              .catch(err => {
                                console.error('Failed to confirm interview attendance', err);
                                XSnackbar.show(m.interview_attendance_error());
                              });
                          }}
                        >
                          <span class="icon-[material-symbols--how-to-reg] w-4 h-4"></span>
                        </button>
                        <button
                          class="btn btn-xs join-item ${
                            application.interviewAttended === false ? 'btn-error' : 'btn-outline btn-error'
                          }"
                          title=${
                            application.interviewAttended === false
                              ? m.interview_attendance_reset_tooltip()
                              : m.applicants_bulk_attendance_no_show_button()
                          }
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            confirmInterviewAttendance(
                              application._id!,
                              application.interviewAttended === false ? null : false,
                            )()
                              .then(() => XSnackbar.show(m.interview_attendance_saved()))
                              .catch(err => {
                                console.error('Failed to confirm interview attendance', err);
                                XSnackbar.show(m.interview_attendance_error());
                              });
                          }}
                        >
                          <span class="icon-[material-symbols--person-off] w-4 h-4"></span>
                        </button>
                      </div>
                    `
                  : nothing
              }
            </div>
          `;
        },
      });

      const localStorageKey = `jobApplicationView:${props.companyID}:${props.jobPositionID}:${props.status}`;
      const built = buildFullOptions({
        class: JobApplication,
        operation: subscribeJobApplicationsWithFilter,
        customColumns: cols,
        selectionSet: {
          userId: 1,
          jobPositionId: 1,
          user: { firstName: 1, lastName: 1, email: 1 },
          matchingScore: selectionSet(() => MatchingScore, 1),
          interviewStart: 1,
          interviewAttended: 1,
        },
        emptyState: () => html`
          <div
            class="py-3 text-sm text-base-content/60 col-span-full flex items-center justify-center"
            style="grid-column: 1 / -1;"
          >
            Keine Daten vorhanden
          </div>
        `,
        isSortable: true,
        isEntryClickable: true,
        entryClickHandler: (entity: JobApplication) => {
          const prefix = window.location.pathname.startsWith('/admin/') ? '/admin' : '/company';
          goTo({ path: `${prefix}/application/${entity._id!}` });
        },
        view: localStorage.getItem(localStorageKey)
          ? constructValue(JSON.parse(localStorage.getItem(localStorageKey)!))
          : new View({
              filter: new AdornisFilter({
                aspects: [
                  new StringAspect({ fieldName: 'jobPositionId', value: props.jobPositionID, mode: 'equals' }),
                  new StringAspect({ fieldName: 'status', value: props.status, mode: 'equals' }),
                ],
              }),
            }),
        onViewChanged(view) {
          localStorage.setItem(localStorageKey, JSON.stringify(view));
        },
      });
      // Deps exclude `props.ids`: it changes on every applicant insert/remove
      // and would tear down the DataTable. Closures read it via propsRef
      // instead. `props.freeSlots` is included so per-row Accept disables
      // when slots run out.
      return [built.dataViewOptions];
    }, [props.companyID, props.jobPositionID, props.status, props.freeSlots]);

    return html`
      <div class="border ${color.border} rounded-box ${color.cardBg} overflow-hidden">
        <div class="px-4 pt-4 pb-2 flex items-center gap-2 ${color.header}">
          <div class="w-1 h-6 rounded-full ${color.bar} mr-2"></div>
          <h3 class="text-lg font-semibold text-base-content">
            ${translateEnum('application_status', props.status)} (${props.ids.length})
          </h3>
        </div>
        <div class="p-2">${DataTable({ options })}</div>
      </div>
    `;
  },
);

export const CompanyJobDetail = stateful((props: { companyID: string; jobPositionID: string }) => {
  const userLoading = useQuery(() => currentUser<User>().result, []);
  if (userLoading.loading) return DefaultContainerLoader();
  if (!userLoading.value) return 'no user';

  const job = useQuery(async () => {
    const result = (await getJobPositionByID(props.jobPositionID)({
      _id: 1,
      title: 1,
      employmentType: 1,
      description: selectionSet(() => TextMultipleLangItem, 1),
      location: 1,
      duration: 1,
      availableSlots: 1,
      startDate: 1,
      companyID: 1,
      expectedAcademicTitle: 1,
      preferredStudyField: 1,
      languageRequirements: 1,
      mandatoryTechSkills: 1,
      desiredTechSkills: 1,
      optionalTechSkills: 1,
      softSkills: 1,
      categoryID: 1,
      meetingLink: 1,
      practicalExperience: selectionSet(() => TextMultipleLangItem, 1),
      timeSlots: selectionSet(() => TimeSlot, 1),
    })) as JobPosition | null;
    return result;
  }, [props.jobPositionID, props.companyID]);

  const numberOfFreeSlotsRemaining = useQuery(
    () => (props.jobPositionID ? subscribeNumberOfFreeSlotsAvailable(props.jobPositionID)() : of(0)),
    [props.jobPositionID],
  );
  if (numberOfFreeSlotsRemaining.loading) return DefaultContainerLoader();
  if (numberOfFreeSlotsRemaining.value === undefined || numberOfFreeSlotsRemaining.value === null)
    return 'error while loading open slots';
  const freeSlots = numberOfFreeSlotsRemaining.value;

  // Single lightweight subscription powers both per-status "select all"
  // (idsByStatus) and the bulk toolbar's slot-exceeded check (statusByID).
  // Replaces what was previously one subscription per status table.
  const allApplications = useQuery(
    () =>
      subscribeJobApplicationsWithFilter(
        0,
        10000,
        new AdornisFilter({
          aspects: [new StringAspect({ fieldName: 'jobPositionId', value: props.jobPositionID, mode: 'equals' })],
        }),
      )({ _id: 1, status: 1, interviewStart: 1, interviewEnd: 1 }),
    [props.jobPositionID],
  );

  const indexes = useMemo(() => {
    const byStatus = new Map<JobApplicationStatus, string[]>();
    const byID = new Map<string, JobApplicationStatus>();
    for (const a of allApplications.value ?? []) {
      if (!a._id || !a.status) continue;
      const arr = byStatus.get(a.status) ?? [];
      arr.push(a._id);
      byStatus.set(a.status, arr);
      byID.set(a._id, a.status);
    }
    return [{ byStatus, byID }];
  }, [allApplications.value]);
  const idsByStatus = indexes.byStatus;
  const statusByID = indexes.byID;
  const bookedApplications = (allApplications.value ?? []).filter(
    application => application.status === JobApplicationStatus.BOOKED,
  );
  const hasInterviewSlots = (job.value?.timeSlots?.length ?? 0) > 0 || bookedApplications.length > 0;
  const addMoreSlots = () =>
    companyJobRouter.goTo('edit', {
      params: { jobPositionID: props.jobPositionID },
      searchParams: new URLSearchParams({ jumpToMeetings: 'true', returnTo: window.location.pathname }),
    });

  const [selectionStore] = useState<SelectionStore>(createSelectionStore());
  const [bulkPending, setBulkPending] = useState(false);

  const runBulk = async (target: JobApplicationStatus, errMsg: string) => {
    const ids = selectionStore.value();
    if (!ids.length) return;
    // Clear BEFORE await — otherwise the destination-status subscription
    // can emit the just-moved rows while selection still contains them,
    // leaving header checkboxes transiently "checked".
    const targetIds = [...ids];
    selectionStore.clear();
    setBulkPending(true);
    try {
      const failures = await updateJobApplicationStatuses(targetIds, target)();
      if (failures > 0) {
        XSnackbar.show(
          failures === targetIds.length
            ? errMsg
            : m.applicants_bulk_partial_failure({ failed: failures, total: targetIds.length }),
        );
      }
    } catch (err) {
      // Server catches per-item errors and reports them via the `failures`
      // count — so this catch only fires on transport/total failure.
      console.error('Bulk status change failed', err);
      XSnackbar.show(errMsg);
    } finally {
      setBulkPending(false);
    }
  };

  const bulkToolbar = obs(
    selectionStore.observable.pipe(
      map(ids => {
        const count = selectionCount(ids);
        if (isEmptySelection(ids)) return nothing;
        // Items already in ACCEPTED/BOOKED don't consume an additional slot
        // (server matches: same-status is a no-op, BOOKED→ACCEPTED is free).
        // Empty lookup map = subscription loading; assume worst case.
        const newlyAcceptedCount =
          statusByID.size === 0
            ? ids.length
            : ids.filter(id => {
                const s = statusByID.get(id);
                return s !== JobApplicationStatus.ACCEPTED && s !== JobApplicationStatus.BOOKED;
              }).length;
        const acceptExceedsSlots = newlyAcceptedCount > freeSlots;
        return html`
          <div
            class="sticky top-2 z-30 bg-base-100 border border-base-300 rounded-box shadow flex flex-wrap items-center gap-2 px-3 py-2 mb-4"
          >
            <span class="font-semibold text-sm">${m.applicants_bulk_selected_count({ count })}</span>
            <div class="flex-1"></div>
            ${renderBulkActionButton({
              enabled: !bulkPending && !acceptExceedsSlots,
              title: acceptExceedsSlots
                ? m.applicants_bulk_accept_not_enough_slots({ free: freeSlots })
                : m.applicants_bulk_accept_button(),
              colorClass: 'btn-success',
              iconClass: 'icon-[material-symbols--check-circle]',
              label: m.applicants_bulk_accept_button(),
              onClick: () => runBulk(JobApplicationStatus.ACCEPTED, m.applicants_bulk_accept_error()),
            })}
            ${renderBulkActionButton({
              enabled: !bulkPending,
              title: m.applicants_bulk_waiting_button(),
              colorClass: 'btn-warning',
              iconClass: 'icon-[material-symbols--schedule]',
              label: m.applicants_bulk_waiting_button(),
              onClick: () => runBulk(JobApplicationStatus.WAITING, m.applicants_bulk_waiting_error()),
            })}
            ${renderBulkActionButton({
              enabled: !bulkPending,
              title: m.applicants_bulk_reject_button(),
              colorClass: 'btn-error',
              iconClass: 'icon-[material-symbols--close]',
              label: m.applicants_bulk_reject_button(),
              onClick: () => runBulk(JobApplicationStatus.REJECTED, m.applicants_bulk_reject_error()),
            })}
            <button
              class="btn btn-sm btn-ghost btn-circle"
              title=${m.applicants_bulk_clear_selection()}
              ?disabled=${bulkPending}
              @click=${() => selectionStore.clear()}
            >
              <span class="icon-[material-symbols--close]"></span>
            </button>
          </div>
        `;
      }),
    ),
  );

  // Bump after each meeting-link save so the displayed link re-renders.
  const [meetingLinkRev, setMeetingLinkRev] = useState(0);
  const editMeetingLink = async () => {
    if (!job.value) return;
    let next: string;
    try {
      next = await promptDialog('Meeting Link', {
        placeholder: 'z.B. https://zoom.eu/...',
        initialValue: job.value.meetingLink ?? '',
        buttonText: 'Speichern',
      });
    } catch {
      return;
    }
    const meetingLink = normalizeHttpUrl(next);
    if (next.trim() && meetingLink === null) {
      void XSnackbar.show(m.validation_url_invalid());
      return;
    }
    job.value.meetingLink = meetingLink;
    await guardDialog(() => job.value!.save());
    setMeetingLinkRev(meetingLinkRev + 1);
  };

  if (job.loading) return MessagedContainerLoader('Loading job details...');
  if (!job.value) return `no job "${props.jobPositionID}"`;

  const basicInfo = [
    job.value.employmentType ? translateEnum('job_form_enum', job.value.employmentType) : undefined,
    job.value.location ?? undefined,
    job.value.startDate ? format(new Date(job.value.startDate), 'P') : undefined,
  ]
    .filter(Boolean)
    .join(' • ');

  return html`
    <div class="p-4 sm:p-8 flex flex-col gap-12">
      <div class="flex flex-col gap-4 ">
        <button
          @click=${() => {
            if (window.location.pathname.startsWith('/admin/')) {
              goTo({ path: `/admin/company/${job.value?.companyID ?? props.companyID}` });
            } else {
              goTo({ path: '/company/' });
            }
          }}
          class="btn btn-ghost self-start"
        >
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.back_to_list()}
        </button>
        <div class="flex items-center gap-3 ml-6">
          <h1 class="text-3xl font-bold text-base-content">${job.value?.title}</h1>
        </div>
      </div>

      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex flex-row justify-between items-start">
            <div class="text-sm text-base-content/70">${basicInfo}</div>
            <button
              class="btn btn-ghost"
              title="Edit"
              @click=${() => companyJobRouter.goTo('edit', { params: { jobPositionID: props.jobPositionID } })}
            >
              <span class="icon-[material-symbols--edit]"></span>
              ${m.edit()}
            </button>
          </div>
        </div>
      </div>

      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex flex-row justify-between items-center">
            <h2 class="card-title text-2xl mb-4">${'Gesprächsliste'}</h2>
            <button
              class="btn btn-primary btn-sm sm:btn-md"
              title=${m.company_job_detail_pdf_export_label()}
              @click=${() =>
                openPopupWithLoadingScreen(
                  servePdfFromComponent(
                    'conversation-list',
                    new URLSearchParams({ jobId: props.jobPositionID, language: getLocale() }).toString(),
                  )(),
                )}
            >
              <span class="icon-[material-symbols--file-export] w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2"></span>
              <span class="hidden sm:inline">${m.company_job_detail_pdf_export_label()}</span>
            </button>
          </div>
          <div class="text-sm text-base-content/70 mb-2">
            Exportieren Sie die Liste aller gebuchten Gespräche als druckbare PDF-Datei.
          </div>
        </div>
      </div>

      ${
        hasInterviewSlots
          ? html`
              <div class="card bg-base-100 shadow">
                <div class="card-body">
                  <div class="flex items-center justify-between gap-4 flex-wrap mb-4">
                    <h2 class="card-title text-2xl">${m.job_detail_available_slots()}</h2>
                    <button class="btn btn-primary btn-sm sm:btn-md" @click=${addMoreSlots}>
                      <span class="icon-[material-symbols--add]"></span>
                      ${m.job_detail_add_more_slots()}
                    </button>
                  </div>
                  <p class="text-sm text-base-content/70 mb-4">
                    Sie können nur so viele Bewerbungen annehmen, wie Sie Slots haben. Aktuell haben Sie noch
                    <strong>${numberOfFreeSlotsRemaining.value}</strong> freie Slots.
                  </p>

                  ${TimeSlotsDisplay({ timeSlots: job.value.timeSlots ?? [], jobPositionID: props.jobPositionID })}
                  ${
                    bookedApplications.length > 0
                      ? html`
                          <div class="divider my-6">${m.job_form_slot_generation_booked_kept()}</div>
                          ${TimeSlotsDisplay({
                            timeSlots: bookedApplications.flatMap(application =>
                              application.interviewStart && application.interviewEnd
                                ? [new TimeSlot({ start: application.interviewStart, end: application.interviewEnd })]
                                : [],
                            ),
                          })}
                        `
                      : nothing
                  }
                </div>
              </div>
            `
          : html`
              <div class="card bg-warning/10 border border-warning/30 shadow">
                <div class="card-body">
                  <div class="flex items-start gap-4">
                    <div class="flex-shrink-0">
                      <span class="icon-[material-symbols--event-upcoming] w-10 h-10 text-warning"></span>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h2 class="card-title text-2xl mb-1">${m.job_detail_no_slots_title()}</h2>
                      <p class="text-sm text-base-content/70 mb-4">${m.job_detail_no_slots_subtitle()}</p>
                      <button
                        class="btn btn-primary btn-sm sm:btn-md"
                        @click=${() =>
                          companyJobRouter.goTo('edit', {
                            params: { jobPositionID: props.jobPositionID },
                            searchParams: new URLSearchParams({
                              jumpToMeetings: 'true',
                              returnTo: window.location.pathname,
                            }),
                          })}
                      >
                        <span class="icon-[material-symbols--add]"></span>
                        ${m.job_detail_no_slots_cta()}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            `
      }

      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex flex-row justify-between items-start gap-4 flex-wrap">
            <div class="min-w-0 flex-1">
              <h2 class="card-title text-2xl mb-1">${m.job_form_meetingLink_label()}</h2>
              ${
                job.value.meetingLink
                  ? html`<a
                      href=${job.value.meetingLink}
                      target="_blank"
                      rel="noopener"
                      class="link link-primary break-all"
                      >${job.value.meetingLink}</a
                    >`
                  : html`<p class="text-sm text-base-content/70">
                      Hinterlegen Sie einen Meeting-Link, der mit Buchungsbestätigungen verschickt wird.
                    </p>`
              }
            </div>
            <button class="btn btn-primary btn-sm sm:btn-md" @click=${editMeetingLink}>
              <span class="icon-[material-symbols--link]"></span>
              ${job.value.meetingLink ? m.company_job_detail_meeting_link_edit() : m.company_job_detail_meeting_link_add()}
            </button>
          </div>
        </div>
      </div>

      ${
        hasInterviewSlots || (allApplications.value?.length ?? 0) > 0
          ? html`
              <div class="card bg-base-100 shadow">
                <div class="card-body">
                  <h2 class="card-title text-2xl mb-4 mt-8">${m.applications()}</h2>
                  ${
                    !hasInterviewSlots
                      ? html`
                          <div class="alert alert-info mb-4">
                            <span>${m.job_detail_applications_no_slots_notice()}</span>
                            <button class="btn btn-primary btn-sm" @click=${addMoreSlots}>
                              ${m.job_detail_no_slots_cta()}
                            </button>
                          </div>
                        `
                      : nothing
                  }
                  ${bulkToolbar}
                  <div class="grid grid-cols-1 gap-6">
                    ${getOrderedStatuses().map(status =>
                      StatusTable({
                        companyID: props.companyID,
                        jobPositionID: props.jobPositionID,
                        status,
                        freeSlots,
                        ids: idsByStatus.get(status) ?? [],
                        store: selectionStore,
                      }),
                    )}
                  </div>
                </div>
              </div>
            `
          : html`
              <div class="card bg-base-100 shadow">
                <div class="card-body">
                  <h2 class="card-title text-2xl mb-4">${m.applications()}</h2>
                  <div
                    class="rounded-box border-2 border-dashed border-base-300 bg-base-200/40 p-8 sm:p-12 flex flex-col items-center text-center gap-3"
                  >
                    <span class="icon-[material-symbols--visibility-off-outline] w-12 h-12 text-base-content/40"></span>
                    <h3 class="text-lg font-semibold text-base-content max-w-xl">${m.job_detail_no_slots_title()}</h3>
                    <p class="text-sm text-base-content/70 max-w-xl">${m.job_detail_no_slots_subtitle()}</p>
                    <button
                      class="btn btn-primary btn-sm mt-2"
                      @click=${() =>
                        companyJobRouter.goTo('edit', {
                          params: { jobPositionID: props.jobPositionID },
                          searchParams: new URLSearchParams({
                            jumpToMeetings: 'true',
                            returnTo: window.location.pathname,
                          }),
                        })}
                    >
                      <span class="icon-[material-symbols--add]"></span>
                      ${m.job_detail_no_slots_cta()}
                    </button>
                  </div>
                </div>
              </div>
            `
      }
    </div>
  `;
});
